import React, { useEffect, useState } from 'react';
import { makeStyles, Typography, Grid, Button, Theme, Hidden } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import LocalVideoPreview from './LocalVideoPreview/LocalVideoPreview';
import SettingsMenu from './SettingsMenu/SettingsMenu';
import { Steps } from '../PreJoinScreens';
import ToggleAudioButton from '../../Buttons/ToggleAudioButton/ToggleAudioButton';
import ToggleVideoButton from '../../Buttons/ToggleVideoButton/ToggleVideoButton';
import { useAppState } from '../../../state';
import useChatContext from '../../../hooks/useChatContext/useChatContext';
import useVideoContext from '../../../hooks/useVideoContext/useVideoContext';

const useStyles = makeStyles((theme: Theme) => ({
  gutterBottom: {
    marginBottom: '1em',
  },
  marginTop: {
    marginTop: '1em',
  },
  hidden: {
    display: 'none',
  },
  deviceButton: {
    width: '100%',
    border: '2px solid #aaa',
    margin: '1em 0',
  },
  localPreviewContainer: {
    paddingRight: '2em',
    [theme.breakpoints.down('sm')]: {
      padding: '0 2.5em',
    },
  },
  joinButtons: {
    display: 'flex',
    justifyContent: 'center',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column-reverse',
      width: '100%',
      '& button': {
        margin: '0.5em 0',
      },
    },
  },
  mobileButtonBar: {
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      justifyContent: 'space-between',
      margin: '1.5em 0 1em',
    },
  },
  mobileButton: {
    padding: '0.8em 0',
    margin: 0,
  },
}));

interface DeviceSelectionScreenProps {
  name: string;
  roomName: string;
  setStep: (step: Steps) => void;
}

export default function DeviceSelectionScreen({ name, roomName, setStep }: DeviceSelectionScreenProps) {
  const classes = useStyles();
  const { getToken, isFetching, setError, isKrispEnabled, isKrispInstalled } = useAppState();
  const { user } = useAppState();
  const [isWaiting, setIsWaiting] = useState<boolean>(false);
  const { connect: chatConnect, setConversationSid } = useChatContext();
  const { connect: videoConnect, isAcquiringLocalTracks, isConnecting } = useVideoContext();
  const disableButtons = isFetching || isAcquiringLocalTracks || isConnecting || isWaiting;
  /*TIMER START*/
  const calculateTimeLeft = () => {
    // @ts-ignore
    const difference = +new Date(user?.timeLeft * 1000) - +new Date();
    let timeLeft = {};

    if (difference > 0) {
      timeLeft = {
        nap: Math.floor(difference / (1000 * 60 * 60 * 24)),
        óra: Math.floor((difference / (1000 * 60 * 60)) % 24),
        perc: Math.floor((difference / 1000 / 60) % 60),
        mp: Math.floor((difference / 1000) % 60),
      };
    }

    return timeLeft;
  };
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());
  const _match = window.location.search.match(/f=([^&]+)/);
  const _forceJoin = _match ? _match[1] : '0';
  const inputRef = React.useRef(null);

  const handleJoin = () => {
    setIsWaiting(true);
    let in_process = false;
    let waiting = false;
    let timer: any = -1;

    const tryJoin = async () => {
      if (in_process) {
        return;
      }

      in_process = true;
      const obj = await getToken(name, roomName);
      const token = obj.token ?? null;
      const conversation_sid = obj.conversation_sid ?? null;

      let msg = '';
      if (obj.toString() === 'invalid token') {
        msg = 'Lejárt a belépési jegyed. Csatlakoz újra a felületről!';
      } else if (obj.toString() === 'ended') {
        msg = 'A megbeszélés lejárt!';
      } else if (obj.toString() === 'access error') {
        msg = 'Hozzáférési hiba, csatlakoz újra a felületről!';
      } else if (obj.toString() === 'waiting') {
        // while waiting dont kick out
        if (timer === -1) {
          timer = setInterval(async () => tryJoin(), 5000);
        }
        waiting = true;
        in_process = false;
        return;
      } else if (obj.toString() !== 'waiting') {
        clearInterval(timer);
        waiting = false;
      }

      setIsWaiting(false);

      if (msg !== '') {
        // @ts-ignore
        setError({
          message: msg,
        });
        in_process = false;
        clearInterval(timer);
        return;
      }
      setConversationSid(conversation_sid);
      videoConnect(token);
      process.env.REACT_APP_DISABLE_TWILIO_CONVERSATIONS !== 'true' && chatConnect(token);
      in_process = false;
      if (!waiting) {
        clearInterval(timer);
      }
    };

    tryJoin();
  };

  useEffect(() => {
    const id = setTimeout(function() {
      setTimeLeft(calculateTimeLeft);
    }, 1000);
    return () => clearTimeout(id);
  });

  useEffect(() => {
    const id = setTimeout(function() {
      // eslint-disable-next-line eqeqeq
      if (_forceJoin == '1' && !disableButtons) {
        // @ts-ignore
        handleJoin();
        clearTimeout(id);

        // eslint-disable-next-line eqeqeq
        if (window.history.pushState) {
          const newurl = window.location.search.replace(/f=([^&]+)/, '');
          window.history.pushState({ path: newurl }, '', newurl);
        }
      }
    }, 1000);
    return () => clearTimeout(id);
  });

  const typography = (
    <Typography variant="h6" color="secondary">
      A megbeszélésről felvétel készül!
    </Typography>
  );
  // @ts-ignore
  // eslint-disable-next-line eqeqeq
  const isRoomRec = user?.isRomRec == 1 ? typography : '';

  if (isFetching || isConnecting || isWaiting) {
    return (
      <Grid container justifyContent="center" alignItems="center" direction="column" style={{ height: '100%' }}>
        <div>
          <CircularProgress variant="indeterminate" />
        </div>
        <div>
          <Typography variant="body2" style={{ fontWeight: 'bold', fontSize: '16px' }}>
            {isWaiting ? 'Várakozás beengedésre...' : 'Belépés...'}
          </Typography>
        </div>
      </Grid>
    );
  }

  // @ts-ignore
  const timerComponents = [];

  Object.keys(timeLeft).forEach(interval => {
    // @ts-ignore
    if (!timeLeft[interval]) {
      return;
    }

    // @ts-ignore
    const intr = timeLeft[interval];
    timerComponents.push(
      <span key={interval}>
        {intr} {interval} {'  '}
      </span>
    );
  });
  const primaryBtn = (
    <Button
      variant="contained"
      color="primary"
      data-cy-join-now
      onClick={handleJoin}
      disabled={disableButtons}
      ref={inputRef}
    >
      Csatlakozás
    </Button>
  );
  // @ts-ignore
  const timerComponent = timerComponents.length ? timerComponents : primaryBtn;
  /*TIMER END*/

  /* hide megbesz. azon*/
  const _matchBar = window.location.search.match(/hideBar=([^&]+)/);
  const _hideBar = !!_matchBar;

  return (
    <>
      <Typography variant="h5" className={_hideBar ? classes.hidden : classes.gutterBottom}>
        {user?.roomName ?? roomName}
        {isRoomRec}
      </Typography>

      <Grid container justifyContent="center">
        <Grid item md={7} sm={12} xs={12}>
          <div className={classes.localPreviewContainer}>
            <LocalVideoPreview identity={user?.displayName ?? ''} />
          </div>
          <div className={classes.mobileButtonBar}>
            <Hidden mdUp>
              <ToggleAudioButton className={classes.mobileButton} disabled={disableButtons} />
              <ToggleVideoButton className={classes.mobileButton} disabled={disableButtons} />
            </Hidden>
            <SettingsMenu mobileButtonClass={classes.mobileButton} />
          </div>
        </Grid>
        <Grid item md={5} sm={12} xs={12}>
          <Grid container direction="column" justifyContent="space-between" style={{ height: '100%' }}>
            <div>
              <Hidden smDown>
                <ToggleAudioButton className={classes.deviceButton} disabled={disableButtons} />
                <ToggleVideoButton className={classes.deviceButton} disabled={disableButtons} />
              </Hidden>
            </div>
            <div className={classes.joinButtons}>{timerComponent}</div>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
}
